import { useState, useEffect } from "react";
import { Link, useLocation } from "@/lib/router-compat";
import { ChevronDown, Menu, X, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import kodiakLogo from "@/assets/kodiak-wordmark.png";

const servicesLinks = [
  { name: "Roof Replacement", path: "/services/roof-replacement" },
  { name: "Roof Repair", path: "/services/roof-repair" },
  { name: "Siding", path: "/services/siding" },
  { name: "Windows", path: "/services/windows" },
  { name: "Gutters", path: "/services/gutters" },
];

const serviceAreasLinks = [
  { name: "Blaine", path: "/locations/blaine" },
  { name: "Eden Prairie", path: "/locations/eden-prairie" },
  { name: "Plymouth", path: "/locations/plymouth" },
  { name: "Woodbury", path: "/locations/woodbury" },
  { name: "St. Paul", path: "/locations/st-paul" },
  { name: "View All Areas", path: "/locations" },
];

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAreasOpen, setMobileAreasOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background shadow-md' : 'bg-background/95 backdrop-blur-sm'}`}>
      <div className="border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3 lg:py-4">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" aria-label="Kodiak Structures & Exteriors - Home">
              <img 
                src={kodiakLogo} 
                alt="Kodiak Structures & Exteriors logo" 
                className="h-14 lg:h-16 w-auto"
                width={200}
                height={64}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">

              {/* Services Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center gap-1 text-foreground hover:text-secondary transition-colors font-medium font-subheading"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                  aria-expanded={servicesOpen}
                  aria-haspopup="true"
                >
                  Services <ChevronDown className="w-4 h-4" aria-hidden="true" />
                </button>
                <div
                  className={`absolute top-full left-0 pt-2 ${servicesOpen ? "block" : "hidden"}`}
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <div className="bg-card rounded-xl shadow-xl border border-border py-2 min-w-[260px]">
                    {servicesLinks.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors"
                      >
                        {service.name}
                      </Link>
                    ))}
                    <Link
                      to="/services"
                      className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors font-medium border-t border-border"
                    >
                      All Services
                    </Link>
                  </div>
                </div>
              </div>

              {/* Service Areas Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center gap-1 text-foreground hover:text-secondary transition-colors font-medium font-subheading"
                  onMouseEnter={() => setAreasOpen(true)}
                  onMouseLeave={() => setAreasOpen(false)}
                  aria-expanded={areasOpen}
                  aria-haspopup="true"
                >
                  Service Areas <ChevronDown className="w-4 h-4" aria-hidden="true" />
                </button>
                <div
                  className={`absolute top-full left-0 pt-2 ${areasOpen ? "block" : "hidden"}`}
                  onMouseEnter={() => setAreasOpen(true)}
                  onMouseLeave={() => setAreasOpen(false)}
                >
                  <div className="bg-card rounded-xl shadow-xl border border-border py-2 min-w-[200px]">
                    {serviceAreasLinks.map((area) => (
                      <Link
                        key={area.path}
                        to={area.path}
                        className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors"
                      >
                        {area.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* About Us Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center gap-1 text-foreground hover:text-secondary transition-colors font-medium font-subheading"
                  onMouseEnter={() => setAboutOpen(true)}
                  onMouseLeave={() => setAboutOpen(false)}
                  aria-expanded={aboutOpen}
                  aria-haspopup="true"
                >
                  About Us <ChevronDown className="w-4 h-4" aria-hidden="true" />
                </button>
                <div
                  className={`absolute top-full right-0 pt-2 ${aboutOpen ? "block" : "hidden"}`}
                  onMouseEnter={() => setAboutOpen(true)}
                  onMouseLeave={() => setAboutOpen(false)}
                >
                  <div className="bg-card rounded-xl shadow-xl border border-border py-2 min-w-[200px]">
                    <Link to="/about" className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors">Our Team</Link>
                    <Link to="/careers" className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors">Careers</Link>
                    <Link to="/projects" className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors">Projects</Link>
                    {/* <Link to="/testimonials" className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors">Testimonials</Link> */}
                  </div>
                </div>
              </div>

              <Link to="/blog" className="text-foreground hover:text-secondary transition-colors font-medium font-subheading">
                Blog
              </Link>

              <Link
                to="/contact"
                className={`text-foreground hover:text-secondary transition-colors font-medium font-subheading ${
                  location.pathname === "/contact" ? "text-secondary" : ""
                }`}
              >
                Contact
              </Link>
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <a href="tel:6519831609" className="flex items-center gap-2 text-foreground font-semibold font-subheading">
                <Phone className="w-4 h-4 text-secondary" />
                (651) 983-1609
              </a>
              <Link to="/contact">
                <Button className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold font-subheading px-6">
                  Free Estimate <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav 
              className="lg:hidden py-4 border-t border-border max-h-[calc(100vh-80px)] overflow-y-auto"
              aria-label="Mobile navigation"
            >
              <div className="space-y-1">
                {/* Services */}
                <button
                  className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium hover:bg-muted"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  aria-expanded={mobileServicesOpen}
                >
                  Services
                  <ChevronDown className={`w-5 h-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {mobileServicesOpen && (
                  <div className="bg-muted/50 py-1">
                    {servicesLinks.map((service) => (
                      <Link key={service.path} to={service.path} className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm" onClick={() => setMobileMenuOpen(false)}>{service.name}</Link>
                    ))}
                    <Link to="/services" className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>All Services</Link>
                  </div>
                )}

                {/* Service Areas */}
                <button
                  className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium hover:bg-muted"
                  onClick={() => setMobileAreasOpen(!mobileAreasOpen)}
                  aria-expanded={mobileAreasOpen}
                >
                  Service Areas
                  <ChevronDown className={`w-5 h-5 transition-transform ${mobileAreasOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {mobileAreasOpen && (
                  <div className="bg-muted/50 py-1">
                    {serviceAreasLinks.map((area) => (
                      <Link key={area.path} to={area.path} className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm" onClick={() => setMobileMenuOpen(false)}>{area.name}</Link>
                    ))}
                  </div>
                )}

                {/* About Us */}
                <button
                  className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium hover:bg-muted"
                  onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                  aria-expanded={mobileAboutOpen}
                >
                  About Us
                  <ChevronDown className={`w-5 h-5 transition-transform ${mobileAboutOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {mobileAboutOpen && (
                  <div className="bg-muted/50 py-1">
                    <Link to="/about" className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm" onClick={() => setMobileMenuOpen(false)}>Our Team</Link>
                    <Link to="/careers" className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm" onClick={() => setMobileMenuOpen(false)}>Careers</Link>
                    <Link to="/projects" className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm" onClick={() => setMobileMenuOpen(false)}>Projects</Link>
                    {/* <Link to="/testimonials" className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm" onClick={() => setMobileMenuOpen(false)}>Testimonials</Link> */}
                  </div>
                )}

                <Link to="/blog" className="block px-4 py-3 text-foreground font-medium hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                <Link to="/contact" className="block px-4 py-3 text-foreground font-medium hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>Contact</Link>

                <div className="px-4 pt-2">
                  <a href="tel:6519831609" className="flex items-center gap-2 text-foreground font-semibold py-2">
                    <Phone className="w-4 h-4 text-secondary" />
                    (651) 983-1609
                  </a>
                </div>

                <div className="px-4 pt-2">
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold">
                      Free Estimate <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                    </Button>
                  </Link>
                </div>
              </div>
            </nav>
          )}
        </div>
      </div>
    </header>
  );
};
